import React, { useEffect, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import GetRecentActivitiesGraph from '../../apis/GetRecentActivitiesGraph/GetRecentActivitiesGraph'

const RecentActivities = ({ idContextTest, userType, userRole, userRecruitmentpartnerId }) => {
  const userId = idContextTest;
  const role = userType === 'Admin' ? userRole : userType;
 
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [PlotComponent, setPlotComponent] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  
  useEffect(() => {
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, 150); 
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

 
  const getResponsiveConfig = () => {
    if (windowWidth <= 480) {
      return {
        height: 200,
        marginLeft: 25,
        marginRight: 5,
        marginTop: 10,
        marginBottom: 50,
        tickFontSize: 8,
        tickAngle: -60,
        barWidth: 0.4,
        dtick: 25,
      };
    } else if (windowWidth <= 768) {
      return {
        height: 220,
        marginLeft: 30,
        marginRight: 8,
        marginTop: 15,
        marginBottom: 60,
        tickFontSize: 9,
        tickAngle: -50,
        barWidth: 0.3,
        dtick: 50,
      };
    } else if (windowWidth <= 1024) {
      return {
        height: 240,
        marginLeft: 35,
        marginRight: 10,
        marginTop: 20,
        marginBottom: 65,
        tickFontSize: 10,
        tickAngle: -45,
        barWidth: 0.28,
        dtick: 50,
      };
    } else if (windowWidth <= 1366) {
      return {
        height: 250,
        marginLeft: 40,
        marginRight: 10,
        marginTop: 24,
        marginBottom: 70,
        tickFontSize: 10,
        tickAngle: -45,
        barWidth: 0.25,
        dtick: 50,
      };
    } else {
      return {
        height: 250,
        marginLeft: 45,
        marginRight: 10,
        marginTop: 24,
        marginBottom: 70,
        tickFontSize: 11,
        tickAngle: -45,
        barWidth: 0.25,
        dtick: 50,
      };
    }
  };

  const responsiveConfig = getResponsiveConfig();
 
  // Prepare plot data
  const order = data.map(item => item?._id);
  const activitiesData = {
    x: order,
    y: order.map(label => {
      const item = data.find(d => d._id === label);
      return item ? item.totalQuantity : 0;
    }),
    type: 'bar',
    width: responsiveConfig.barWidth,
    marker: {
      color: ['#31b3f8', '#FFC107', '#FF9800', '#FF5722', '#4CAF50', '#000000'],
    },
    hovertemplate: '%{x}<br><b>%{y}</b> candidates<extra></extra>',
  };
 
  useEffect(() => {
    let isMounted = true;

    const loadPlotly = async () => {
      try {
        const [plotlyModule, factoryModule] = await Promise.all([
          import('plotly.js-cartesian-dist-min'),
          import('react-plotly.js/factory'),
        ]);

        if (!isMounted) {
          return;
        }

        const Plotly = plotlyModule.default ?? plotlyModule;
        const createPlotlyComponent = factoryModule.default ?? factoryModule;
        setPlotComponent(() => createPlotlyComponent(Plotly));
      } catch (err) {
        console.error('Error loading Plotly:', err);
      }
    };

    loadPlotly();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const fetchGraphData = async () => {
      setIsLoading(true);
      try {
        const res = await GetRecentActivitiesGraph({
          id: userId,
          role,
          recruitmentpartnerId: userRecruitmentpartnerId,
        });
 
        setData(Array.isArray(res.data.data) ? res.data.data : []);
      } catch (err) {
        console.error('Error fetching graph data:', err);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };
 
    fetchGraphData();
  }, [userId, role, userRecruitmentpartnerId, userType]);
 
  return (
    <div className="bento">
      <div className="bento-header">
        <div className="bento-header-content">
          <div className="bento-header-text">
            <span>Recent Activities (Last 30 Days)</span>
          </div>
        </div>
      </div>
 
      <div
        className="activities-chart-container"
        style={{
          display: 'block',
          position: 'relative',
          zIndex: 1,
          width: '100%',
          minHeight: `${responsiveConfig.height}px`,
          overflow: 'visible',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {isLoading || !PlotComponent ? (
          <div
            style={{
              height: `${responsiveConfig.height}px`,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0px 3px 8px #0000003d',
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <PlotComponent
            key={`chart-${windowWidth}`}
            data={[activitiesData]}
            layout={{
              margin: { 
                l: responsiveConfig.marginLeft, 
                r: responsiveConfig.marginRight, 
                t: responsiveConfig.marginTop, 
                b: responsiveConfig.marginBottom 
              },
              paper_bgcolor: 'transparent',
              plot_bgcolor: 'transparent',
              showlegend: false,
              legend: { visible: false },
              font: {
                family: 'Segoe UI, sans-serif',
                size: responsiveConfig.tickFontSize,
                color: '#666',
              },
              xaxis: {
                showgrid: false,
                showline: false,
                zeroline: false,
                tickfont: { size: responsiveConfig.tickFontSize, color: '#888' },
                tickangle: responsiveConfig.tickAngle,
                automargin: true,
              },
              yaxis: {
                showgrid: true,
                gridcolor: 'rgba(0,0,0,0.1)',
                showline: false,
                zeroline: false,
                tickfont: { size: responsiveConfig.tickFontSize, color: '#888' },
                dtick: responsiveConfig.dtick,
                automargin: true,
              },
              bargap: 0.55,
              hovermode: 'closest',
              autosize: true,
              responsive: true,
              transition: { duration: 300, easing: 'cubic-in-out' },
            }}
            config={{
              displayModeBar: false,
              responsive: true,
            }}
            style={{ 
              width: '100%', 
              height: `${responsiveConfig.height}px`, 
              display: 'block', 
              transition: 'all 0.3s ease-in-out' 
            }}
            useResizeHandler={true}
          />
        )}
      </div>
    </div>
  );
};
 
export default RecentActivities;